import { useMutation } from '@tanstack/react-query';
import type { LoginTenant } from '@alter/types';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getApi } from '../../api/bootstrap';
import { Button } from '../../components/Button';
import { PiePagina } from '../../components/PiePagina';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Spinner } from '../../components/Spinner';
import { formatError } from '../../lib/formatError';
import { useAuthStore } from '../../stores/authStore';

interface LocationState {
  selectorToken: string;
  tenants: LoginTenant[];
}

function isLocationState(value: unknown): value is LocationState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'selectorToken' in value &&
    typeof value.selectorToken === 'string' &&
    'tenants' in value &&
    Array.isArray(value.tenants)
  );
}

export function SeleccionarTenantPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const mutation = useMutation({
    mutationFn: ({ selectorToken, tenantId }: { selectorToken: string; tenantId: string }) =>
      getApi().authClient.seleccionarTenant({ selectorToken, tenantId }),
    onSuccess: (token) => {
      useAuthStore.getState().setSession({
        token: token.access_token,
        tipoToken: token.tipo,
        locale: token.locale,
        tenantNombre: token.tenant_nombre,
        usuarioNombre: token.usuario_nombre,
      });
      navigate('/mi-simulacion', { replace: true });
    },
  });

  // Guard: si entran a /tenant sin estado válido, mandar a /login.
  if (!isLocationState(state)) {
    return <Navigate to="/login" replace />;
  }

  const { selectorToken, tenants } = state;

  return (
    <main className="flex min-h-screen flex-col bg-slate-50 px-4">
      <div className="flex flex-1 items-center justify-center py-6">
        <div className="w-full max-w-md space-y-6 rounded-2xl bg-white p-6 shadow-sm sm:p-8">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Seleccionar institución</h1>
            <p className="mt-1 text-sm text-slate-500">Elegí en qué tenant querés ingresar.</p>
          </div>
          <ul className="space-y-2">
            {tenants.map((t) => (
              <li key={t.id}>
                <Button
                  onClick={() => {
                    mutation.mutate({ selectorToken, tenantId: t.id });
                  }}
                  disabled={mutation.isPending}
                  variant="secondary"
                  className="w-full justify-start"
                >
                  {mutation.isPending && mutation.variables?.tenantId === t.id ? <Spinner /> : null}
                  {t.nombre}
                </Button>
              </li>
            ))}
          </ul>
          {mutation.isError && <ErrorMessage>{formatError(mutation.error)}</ErrorMessage>}
        </div>
      </div>
      <PiePagina />
    </main>
  );
}
